import type { ButtonHTMLAttributes } from "react";

const base =
  "inline-flex items-center justify-center rounded-lg text-sm font-medium transition-colors duration-75 " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70 focus-visible:ring-offset-2 " +
  "focus-visible:ring-offset-bg disabled:pointer-events-none disabled:opacity-50";

/** One primary action per screen. Amber is the door's colour; spend it sparingly. */
export function Button(props: ButtonHTMLAttributes<HTMLButtonElement>) {
  const { className = "", ...rest } = props;
  return (
    <button
      {...rest}
      className={`${base} h-11 px-5 bg-accent text-accent-fg hover:bg-accent-soft active:bg-accent-strong ${className}`}
    />
  );
}

/**
 * Secondary's look as a class, for the anchor that must match the button beside
 * it (a download link next to a confirm button is one family of controls).
 */
export const secondaryClass = `${base} h-11 px-5 border border-border-strong text-text hover:border-border-loud hover:bg-surface`;

/** Secondary — present, obviously clickable, not competing. */
export function SecondaryButton(props: ButtonHTMLAttributes<HTMLButtonElement>) {
  const { className = "", ...rest } = props;
  return <button {...rest} className={`${secondaryClass} ${className}`} />;
}

/** Navigation, not action: back out of a screen without competing with it. */
export function LinkButton(props: ButtonHTMLAttributes<HTMLButtonElement>) {
  const { className = "", ...rest } = props;
  return (
    <button
      {...rest}
      className={
        "text-sm text-faint underline underline-offset-4 transition-colors duration-75 " +
        "hover:text-text-soft focus-visible:outline-none focus-visible:ring-2 " +
        "focus-visible:ring-focus/70 focus-visible:ring-offset-2 focus-visible:ring-offset-bg " +
        "disabled:pointer-events-none disabled:opacity-50 " +
        className
      }
    />
  );
}

/**
 * Chip-sized, amber-edged: the one action worth taking in a row of quiet
 * controls. Sized to sit flush beside a `CopyButton` — the share rows and the
 * directory offer (M17) are both a chip and its neutral siblings, side by side.
 *
 * Exported as a class because the primary action in a row is not always a
 * button: the share sheet's "Post on X" is an anchor at an intent URL, and it
 * must look identical to the chips it stands beside.
 */
export const chipClass =
  "inline-flex h-9 items-center rounded-md border border-accent/50 px-3 text-xs " +
  "font-medium text-accent-ink-strong transition-colors duration-75 hover:bg-accent/10 " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70 " +
  "focus-visible:ring-offset-2 focus-visible:ring-offset-bg " +
  "disabled:pointer-events-none disabled:opacity-50";

export function ChipButton(props: ButtonHTMLAttributes<HTMLButtonElement>) {
  const { className = "", ...rest } = props;
  return <button {...rest} className={`${chipClass} ${className}`} />;
}

/** Quiet but visible — "Skip" must never feel hidden (PRD §2.2.3: all skippable). */
export function QuietButton(props: ButtonHTMLAttributes<HTMLButtonElement>) {
  const { className = "", ...rest } = props;
  return (
    <button {...rest} className={`${base} h-11 px-3 text-muted hover:text-text ${className}`} />
  );
}
